import * as pako from 'pako';

import {
    get,
    set,
    omit,
    pick,
    isArray,
    mergeWith,
    cloneDeep,
    isString,
} from 'lodash';

import { data } from '@paychex/core';

import { withTraceability } from './traceability';
import { Fetch, RetryFunction } from '@paychex/core/types/data';
import { Tracker } from '@paychex/core/types/trackers';

import type { Diagnostics } from './diagnostics';

/**
 * Values needed to create shared logic.
 */
export interface SharedLogicOptions {

    /** The Tracker to use for traceability and performance events. */
    tracker: Tracker,

    /** The Diagnostics function to invoke when a data call is aborted. */
    diagnostics: Diagnostics,

}

const {
    falloff,
    withHeaders,
    withRetry,
    withXSRF,
    withDiagnostics,
} = data.utils;

const ENSURE_HEADERS = 'application/vnd.paychex.com+v1+json, application/json, text/plain, */*';

function concatArrays(lhs: any, rhs: any): any {
    if (isArray(lhs))
        return lhs.concat(rhs);
}

/**
 * Creates performance timings for data calls using the specified Tracker if
 * the Request's `ignore.tracking` property is not set to `true`.
 *
 * @param fetch The fetch method to wrap.
 * @param tracker The Tracker to use to create performance events.
 * @returns A fetch method that creates a timer entry in the specified Tracker.
 */
export function withPerformance(fetch: Fetch, tracker: Tracker): Fetch {
    return async function usePerformance(request) {
        if (get(request, 'ignore.tracking', false))
            return fetch(request);
        const label = get(request, 'tracking.label',
            get(request, 'headers.x-payx-bizpn',
                request.url));
        const stop = tracker.start(label);
        const response = await fetch(request);
        const txid = get(response, 'meta.headers.x-payx-txid');
        const correlationId = get(response, 'data.correlationId');
        if (!get(response, 'meta.error'))
            stop(mergeWith({
                txid,
                correlationId,
                tags: ['http'],
                category: 'network',
                request: pick(request, ['base', 'path', 'method']),
                retryCount: get(response, 'meta.retryCount', 0),
                cached: get(response, 'meta.cached', false),
            }, omit(request.tracking, 'label'), concatArrays));
        return response;
    } as Fetch;
}

/**
 * Compresses request payloads if the `compress` property is set to `true`. Most Paychex
 * endpoints can automatically reinflate payloads deflated by this method.
 *
 * @param {Fetch} fetch The fetch method to wrap.
 * @returns {Fetch} A fetch method that compresses request payloads if the `compress`
 * property is set to `true`.
 */
export function withCompression(fetch: Fetch): Fetch {
    return async function useCompression(request) {
        if (get(request, 'compress', false)) {
            const clone = cloneDeep(request);
            clone.headers['content-encoding'] = 'deflate';
            clone.body = pako.deflate(
                isString(clone.body)
                    ? clone.body
                    : JSON.stringify(clone.body));
            return fetch(clone);
        }
        return fetch(request);
    } as Fetch;
}

/**
 * Sets `withCredentials` on outgoing requests, which sends client-side cookies.
 *
 * @param fetch The fetch method to wrap.
 * @returns A fetch method that modifies requests to set `withCredentials` true.
 */
export function withCredentials(fetch: Fetch): Fetch {
    return async function useCredentials(request) {
        if ('withCredentials' in request)
            return fetch(request);
        const clone = cloneDeep(request);
        clone.withCredentials = true;
        return fetch(clone);
    } as Fetch;
}

function ifNonClientErrorGETs(retry: RetryFunction): RetryFunction {
    // only retry GETs that had non-4xx responses
    return async function retryIfMethod(request, response) {
        if (request.method === 'GET' && !/^4/.test(String(response.status))) {
            return retry(request, response);
        }
        return Promise.reject();
    };
}

/**
 * Adds standard Paychex Accept headers.
 *
 * @param fetch The fetch method to wrap.
 * @returns A fetch method that includes standard Paychex Accept headers.
 */
export function withEnsureHeaders(fetch: Fetch): Fetch {
    return async function ensureHeaders(request) {
        if (!get(request, 'headers.accept')) {
            set(request, 'headers.accept', ENSURE_HEADERS);
        }
        return fetch(request);
    } as Fetch;
}

const storage: Pick<Storage, 'getItem' | 'setItem'> = (function createStorage() {
    const cache = new Map<string, any>();
    return get(window, 'sessionStorage', {
        getItem: cache.get.bind(cache),
        setItem: cache.set.bind(cache),
    });
})();

/**
 * Wraps a given fetch method to provide common Paychex-specific data call functionality. Includes
 * all the functionality also exported by this module, including:
 *
 * - adds withCredentials to requests
 * - adds traceability headers to requests
 * - adds cross-site request forgery logic
 * - adds default accept headers for Paychex endpoints
 * - create traceability events
 * - create performance measures
 * - compress request bodies when desired
 * - report diagnostics when calls are aborted
 * - retry failed (non-4xx) GET requests
 *
 * @param fetch The fetch method to wrap with shared Paychex logic.
 * @param options Values needed to create shared logic.
 * @returns A fetch function that includes shared Paychex data call logic.
 * @example
 * ```js
 * import { fetch, createRequest } from '~/path/to/datalayer.js';
 * import tracker from '~/path/to/tracker.js';
 *
 * const diagnostics = internal.data.utils.createDiagnostics({
 *   fetch,
 *   createRequest,
 *   tracker,
 * });
 *
 * const pipeline = internal.data.utils.withSharedLogic(fetch, { tracker, diagnostics });
 * ```
 */
export function withSharedLogic(fetch: Fetch, { tracker, diagnostics }: SharedLogicOptions): Fetch {

    let shared = fetch;

    const retry = ifNonClientErrorGETs(falloff(2, 500));
    const sid = storage.getItem('token') || tracker.uuid();

    shared = withCredentials(shared);
    shared = withTraceability(shared, tracker);

    // everything above this point will be retried on error;

    shared = withRetry(shared, retry);

    // everything below will run before the first retry as
    // well as after all the retries have completed

    shared = withCompression(shared);
    shared = withPerformance(shared, tracker);
    shared = withDiagnostics(shared, diagnostics);
    shared = withXSRF(shared, { hosts: ['*.paychex.com'] });
    shared = withEnsureHeaders(shared);
    shared = withHeaders(shared, {
        'x-payx-sid': sid,
    });

    storage.setItem('token', sid);

    return shared;

}
